import { useQuery } from '@tanstack/react-query';
import { get, getTodos } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import type { CategoriaFin, CentroFin, ContaFin, Pagina } from './tipos';
import { rotuloCategoria } from '../../nucleo/rotulos';
import { opcoesCategoriasFinanceiro } from '../../nucleo/categorias-financeiro';
import { rotuloForma } from '../../nucleo/forma-pagamento';

/**
 * Review r25 spec (B2): as listas do Financeiro (categorias, centros, contas) só com o módulo instalado e `financeiro.ver`,
 * como as formas de pagamento abaixo. Sem isso a API responde 403 e o console mostrava o erro (a ficha do documento fica
 * montada na lista, então quem só tem `contas.ver` via o 403 ao abrir A receber).
 */
export function useVerFinanceiro() {
  const s = useSessao();
  return s.instalado('financeiro') && s.pode('financeiro.ver');
}

/** Categorias do Financeiro: receita para fatura, despesa para os demais tipos (a baixa vira lançamento no mesmo sentido). */
export function useCategoriasFin() {
  // Review r25 robustez (M3): a árvore inteira na chave única; review r25 spec (B2): só com o Financeiro e financeiro.ver.
  const habilitado = useVerFinanceiro();
  return useQuery({ ...opcoesCategoriasFin(), enabled: habilitado });
}

/** Review r25 robustez (M3): a mesma consulta (árvore inteira) do Financeiro, na mesma chave; antes, só a 1ª página. */
export const opcoesCategoriasFin = () => opcoesCategoriasFinanceiro<CategoriaFin>();

/** Fase 2, W7: centros de custo do Financeiro (documento e item). */
export function useCentrosFin() {
  const habilitado = useVerFinanceiro();
  return useQuery({ queryKey: ['/financeiro/centros-custo', 'lista'], queryFn: () => get<Pagina<CentroFin>>('/financeiro/centros-custo'), staleTime: 30_000, enabled: habilitado });
}

export function useContasFin() {
  const habilitado = useVerFinanceiro();
  return useQuery({ queryKey: ['/financeiro/contas', 'lista', true], queryFn: () => get<Pagina<ContaFin>>('/financeiro/contas', { ativa: true }), staleTime: 30_000, enabled: habilitado });
}

/** Nome da categoria com o caminho dos pais: "Receitas operacionais / Prestação de serviços". */
export function caminhoDaCategoria(c: CategoriaFin, todas: CategoriaFin[]): string {
  const nomes = [rotuloCategoria(c.nome)];
  let atual: CategoriaFin | undefined = c;
  for (let i = 0; i < 6 && atual?.parent_id; i++) {
    atual = todas.find((x) => x.id === atual!.parent_id);
    if (atual) nomes.unshift(rotuloCategoria(atual.nome));
  }
  return nomes.join(' / ');
}

/** Opções de categoria folha, ativa, analítica (fase 2, W24: a sintética só agrupa) e do tipo certo para o documento. */
export function opcoesDeCategoria(todas: CategoriaFin[], receita: boolean) {
  const tipo = receita ? 'receita' : 'despesa';
  return todas
    .filter((c) => c.tipo === tipo && c.ativa && !c.sintetica && !todas.some((f) => f.parent_id === c.id))
    .map((c) => ({ valor: c.id, rotulo: caminhoDaCategoria(c, todas) }));
}

/**
 * Fase 2 (W24, K9, P4): formas de pagamento ativas do catálogo do Cadastros (a mesma lista do Compras), só para quem lê o
 * Cadastros. A chave começa por /cadastros/formas-pagamento para invalidar junto com o catálogo.
 */
export function useFormasPagamento() {
  const s = useSessao();
  const habilitado = s.instalado('cadastros') && s.pode('cadastros.ver');
  const q = useQuery({
    queryKey: ['/cadastros/formas-pagamento', 'ativas'],
    queryFn: () => getTodos<{ id: string; nome: string; tipo?: string; abreviatura?: string | null }>('/cadastros/formas-pagamento', { ativa: true }),
    enabled: habilitado,
    staleTime: 30_000,
  });
  return { habilitado, opcoes: (q.data?.itens ?? []).map((f) => ({ valor: f.id, rotulo: rotuloForma(f) })) };
}
